import React, { useRef, useState } from "react"
import { Camera, Close, Done, Upload } from "./images"

interface FaceCaptureModalProps {
  onCapture: (base64: string) => void
  onCancel: () => void
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}

function FaceCaptureModal({
  onCapture,
  onCancel
}: FaceCaptureModalProps): React.ReactNode {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isHoveringPrimary, setIsHoveringPrimary] = useState(false)
  const [isHoveringCancel, setIsHoveringCancel] = useState(false)
  const [isHoveringClose, setIsHoveringClose] = useState(false)

  const isMobile = /Mobi|Android/i.test(navigator.userAgent)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    setIsProcessing(true)
    try {
      const base64 = await fileToBase64(file)
      setTimeout(() => {
        onCapture(base64)
      }, 300)
    } catch (error) {
      console.error("[Vesant SDK] Error processing image:", error instanceof Error ? error.message : "Unknown error")
      setIsProcessing(false)
    }
  }

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999,
    padding: "16px",
    animation: "fadeIn 0.2s ease-out"
  }

  const modalStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    width: "100%",
    maxWidth: "448px",
    animation: "zoomIn 0.2s ease-out"
  }

  const headerStyle: React.CSSProperties = {
    position: "relative",
    padding: "24px 24px 16px",
    borderBottom: "1px solid #f3f4f6"
  }

  const closeButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: "16px",
    right: "16px",
    padding: "8px",
    background: isHoveringClose ? "#f3f4f6" : "transparent",
    border: "none",
    borderRadius: "9999px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }

  const titleStyle: React.CSSProperties = {
    fontSize: "24px",
    fontWeight: 600,
    color: "#111827",
    margin: 0
  }

  const subtitleStyle: React.CSSProperties = {
    fontSize: "14px",
    color: "#6b7280",
    marginTop: "4px"
  }

  const contentStyle: React.CSSProperties = {
    padding: "32px 24px"
  }

  const visualGuideContainerStyle: React.CSSProperties = {
    marginBottom: "32px",
    display: "flex",
    justifyContent: "center"
  }

  const visualGuideStyle: React.CSSProperties = {
    position: "relative"
  }

  const circleStyle: React.CSSProperties = {
    width: "128px",
    height: "128px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(0, 188, 125, 0.2) 0%, rgba(0, 188, 125, 0.05) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }

  const badgeStyle: React.CSSProperties = {
    position: "absolute",
    top: "-4px",
    right: "-4px",
    width: "32px",
    height: "32px",
    background: "#ffffff",
    borderRadius: "50%",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }

  const instructionsBoxStyle: React.CSSProperties = {
    background: "linear-gradient(135deg, #f9fafb 0%, rgba(243, 244, 246, 0.5) 100%)",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "24px"
  }

  const instructionsTitleStyle: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 500,
    color: "#111827",
    marginBottom: "8px"
  }

  const instructionsListStyle: React.CSSProperties = {
    fontSize: "12px",
    color: "#6b7280",
    listStyle: "none",
    padding: 0,
    margin: 0
  }

  const instructionItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "6px"
  }

  const bulletStyle: React.CSSProperties = {
    color: "#00bc7d",
    marginRight: "8px",
    flexShrink: 0
  }

  const buttonsContainerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "12px"
  }

  const primaryButtonStyle: React.CSSProperties = {
    width: "100%",
    background: isProcessing ? "#d1d5db" : (isHoveringPrimary ? "#00a86d" : "#00bc7d"),
    color: "#ffffff",
    fontWeight: 500,
    padding: "14px 24px",
    borderRadius: "12px",
    border: "none",
    cursor: isProcessing ? "not-allowed" : "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    boxShadow: isProcessing ? "none" : (isHoveringPrimary ? "0 20px 25px -5px rgba(0, 188, 125, 0.3)" : "0 10px 15px -3px rgba(0, 188, 125, 0.2)"),
    fontSize: "16px"
  }

  const cancelButtonStyle: React.CSSProperties = {
    width: "100%",
    background: isProcessing ? "#f3f4f6" : (isHoveringCancel ? "#f9fafb" : "#ffffff"),
    color: "#374151",
    fontWeight: 500,
    padding: "14px 24px",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    cursor: isProcessing ? "not-allowed" : "pointer",
    transition: "all 0.2s",
    fontSize: "16px"
  }

  const footerStyle: React.CSSProperties = {
    padding: "0 24px 24px"
  }

  const footerTextStyle: React.CSSProperties = {
    fontSize: "12px",
    textAlign: "center",
    color: "#9ca3af",
    margin: 0
  }

  const spinnerStyle: React.CSSProperties = {
    width: "20px",
    height: "20px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTopColor: "#ffffff",
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite"
  }

  const hiddenInputStyle: React.CSSProperties = {
    display: "none"
  }

  // Add keyframe animations via style tag
  const styleTag = (
    <style>
      {`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}
    </style>
  )

  return (
    <>
      {styleTag}
      <div style={overlayStyle}>
        <div style={modalStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <button
              onClick={onCancel}
              onMouseEnter={() => setIsHoveringClose(true)}
              onMouseLeave={() => setIsHoveringClose(false)}
              style={closeButtonStyle}
              aria-label="Close"
            >
              <img src={Close} alt="Close Icon" width={16} height={16} />
            </button>
            <h2 style={titleStyle}>Face Verification</h2>
            <p style={subtitleStyle}>Please capture or upload a clear photo of your face</p>
          </div>

          {/* Content */}
          <div style={contentStyle}>
            {/* Visual Guide */}
            <div style={visualGuideContainerStyle}>
              <div style={visualGuideStyle}>
                <div style={circleStyle}>
                  <img src={Camera} alt="Camera Icon" width={48} height={48} />
                </div>
                <div style={badgeStyle}>
                  <img src={Done} alt="Check Icon" width={16} height={16} />
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div style={instructionsBoxStyle}>
              <h3 style={instructionsTitleStyle}>Tips for best results:</h3>
              <ul style={instructionsListStyle}>
                <li style={instructionItemStyle}>
                  <span style={bulletStyle}>•</span>
                  <span>Ensure good lighting on your face</span>
                </li>
                <li style={instructionItemStyle}>
                  <span style={bulletStyle}>•</span>
                  <span>Remove glasses or accessories if possible</span>
                </li>
                <li style={instructionItemStyle}>
                  <span style={bulletStyle}>•</span>
                  <span>Look directly at the camera</span>
                </li>
              </ul>
            </div>

            {/* Hidden file input */}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture={isMobile ? "user" : undefined}
              style={hiddenInputStyle}
              onChange={handleFileChange}
              disabled={isProcessing}
            />

            {/* Action Buttons */}
            <div style={buttonsContainerStyle}>
              <button
                onClick={() => inputRef.current?.click()}
                onMouseEnter={() => setIsHoveringPrimary(true)}
                onMouseLeave={() => setIsHoveringPrimary(false)}
                disabled={isProcessing}
                style={primaryButtonStyle}
              >
                {isProcessing ? (
                  <>
                    <div style={spinnerStyle} />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    {isMobile ? (
                      <img src={Camera} alt="Camera Icon" width={20} height={20} />
                    ) : (
                      <img src={Upload} alt="Upload Icon" width={20} height={20} />
                    )}
                    <span>{isMobile ? "Capture Photo" : "Upload Photo"}</span>
                  </>
                )}
              </button>

              <button
                onClick={onCancel}
                onMouseEnter={() => setIsHoveringCancel(true)}
                onMouseLeave={() => setIsHoveringCancel(false)}
                disabled={isProcessing}
                style={cancelButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Footer Note */}
          <div style={footerStyle}>
            <p style={footerTextStyle}>
              Your photo will be securely processed and used only for verification purposes
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default FaceCaptureModal